import { useState } from 'react'
import { format } from 'date-fns'
import { enUS, fr } from 'date-fns/locale'
import { CalendarDays, FileText, Loader2, Phone, PhoneIncoming, Play, PhoneOutgoing, Sparkles, User, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { formatPhoneDisplay } from '@/lib/phoneValidation'
import { useCallTranscription } from '@/api/callSummary'
import type { TimelineEvent } from '@/types/timeline'
import { ActionsResumeAppel } from './ActionsResumeAppel'
import { DossierClio } from './DossierClio'
import { LecteurAppel } from './LecteurAppel'

interface Props {
  event: TimelineEvent
  phoneNumber: string
  darkMode: boolean
  onClose: () => void
}

const APERCU = 4
// Une couleur stable par interlocuteur, dans l'ordre où il prend la parole.
const TEINTES = ['text-indigo-600', 'text-emerald-600', 'text-amber-600']
const TEINTES_SOMBRE = ['text-indigo-300', 'text-emerald-300', 'text-amber-300']
const POINTS = ['bg-indigo-500', 'bg-emerald-500', 'bg-amber-500']

function duree(s?: number) {
  if (!s) return ''
  const m = Math.floor(s / 60)
  return `${m}:${String(s % 60).padStart(2, '0')}`
}

/**
 * Panneau « Détails » d'un appel, ouvert depuis sa carte dans le fil : une
 * carte pour l'appel, une pour le résumé et ses actions, une pour la
 * transcription, et le lecteur en pied.
 */
export function DetailsAppel({ event, phoneNumber, darkMode, onClose }: Props) {
  const { t, i18n } = useTranslation()
  const [tout, setTout] = useState(false)
  const callId = Number(event.id.slice('call_'.length))
  const { data, isLoading } = useCallTranscription(callId)
  const sortant = event.type === 'call_outbound'
  const locale = i18n.language?.startsWith('en') ? enUS : fr
  const quand = format(new Date(event.timestamp), i18n.language?.startsWith('en') ? "MMM d, yyyy 'at' h:mm a" : "d MMM yyyy 'à' HH:mm", { locale })
  const resume = event.details.ai_summary || null
  const ligneBrute = sortant ? event.details.from_number : event.details.to_number
  const ligne = ligneBrute && ligneBrute.startsWith('+') ? formatPhoneDisplay(ligneBrute) : ''

  const locuteurs: string[] = []
  const repliques = (data?.transcript_by_speaker || '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const i = l.indexOf(' : ')
      const qui = i > 0 ? l.slice(0, i) : ''
      if (qui && !locuteurs.includes(qui)) locuteurs.push(qui)
      return { qui, texte: i > 0 ? l.slice(i + 3) : l }
    })
  const visibles = tout ? repliques : repliques.slice(0, APERCU)

  const sourdine = darkMode ? 'text-slate-400' : 'text-slate-500'
  const texte = darkMode ? 'text-slate-100' : 'text-slate-800'
  const carte = cn('rounded-xl border', darkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)]')
  const titreCarte = (icone: ReactNode, label: string, droite?: ReactNode) => (
    <div className="flex items-center gap-2 px-4 pt-3.5 pb-2">
      {icone}
      <span className={cn('text-[12.5px] font-semibold', texte)}>{label}</span>
      {droite && <span className={cn('ml-auto text-[11px]', sourdine)}>{droite}</span>}
    </div>
  )
  const info = (icone: ReactNode, label: string, valeur: string) => (
    <div className="flex items-center gap-2.5 text-[12px]">
      <span className={cn('flex items-center gap-1.5 w-[92px] flex-shrink-0', sourdine)}>
        {icone}
        {label}
      </span>
      <span className={cn('min-w-0 truncate tabular-nums', texte)}>{valeur}</span>
    </div>
  )

  return (
    <div className={cn('flex flex-col w-full h-full', darkMode ? 'bg-slate-950' : 'bg-slate-50')}>
      <div className={cn('flex items-center justify-between px-4 h-14 flex-shrink-0 border-b', darkMode ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white')}>
        <h3 className={cn('text-[13.5px] font-semibold', texte)}>{t('callDetail.title', "Détails de l'appel")}</h3>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('callDetail.close', 'Fermer')}
          className={cn('p-1.5 rounded-lg transition-colors', darkMode ? 'text-slate-400 hover:bg-slate-800 hover:text-white' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800')}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-3">
        <section className={cn(carte, 'p-4 flex flex-col gap-3.5')}>
          <div className="flex items-center gap-3">
            <div className={cn('w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0', sortant ? 'bg-emerald-500' : 'bg-indigo-600')}>
              {sortant ? <PhoneOutgoing className="w-4 h-4 text-white" /> : <PhoneIncoming className="w-4 h-4 text-white" />}
            </div>
            <div className={cn('text-[14px] font-semibold', texte)}>
              {sortant ? t('contacts.timeline.types.outboundCall', 'Appel sortant') : t('contacts.timeline.types.inboundCall', 'Appel entrant')}
            </div>
            {event.details.duration ? (
              <span className={cn('ml-auto rounded-full px-2.5 py-0.5 text-[11.5px] font-medium tabular-nums', darkMode ? 'bg-slate-800 text-slate-300' : 'bg-slate-100 text-slate-600')}>
                {duree(event.details.duration)}
              </span>
            ) : null}
          </div>
          <div className={cn('flex flex-col gap-2 pt-3 border-t', darkMode ? 'border-slate-800' : 'border-slate-100')}>
            {info(<CalendarDays className="h-3.5 w-3.5" />, t('callDetail.date', 'Date'), quand)}
            {event.agent_name &&
              info(<User className="h-3.5 w-3.5" />, sortant ? t('callDetail.madeBy', 'Fait par') : t('callDetail.answeredBy', 'Répondu par'), event.agent_name)}
            {ligne && info(<Phone className="h-3.5 w-3.5" />, t('callDetail.line', 'Ligne'), ligne)}
            <DossierClio callId={callId} phoneNumber={phoneNumber} darkMode={darkMode} variante="details" />
          </div>
        </section>

        <section className={carte}>
          {titreCarte(<Sparkles className="h-3.5 w-3.5 text-indigo-500" />, t('callDetail.aiSummary', 'Résumé IA'))}
          <div className="px-4 pb-3 flex flex-col gap-2.5">
            {resume && <p className={cn('text-[12.5px] leading-relaxed', texte)}>{resume}</p>}
            <div className={cn(resume && 'pt-2 border-t', darkMode ? 'border-slate-800' : 'border-slate-100')}>
              <ActionsResumeAppel callId={callId} resume={resume} darkMode={darkMode} />
            </div>
          </div>
        </section>

        <section className={carte}>
          {titreCarte(
            <FileText className={cn('h-3.5 w-3.5', sourdine)} />,
            t('callDetail.tabTranscript', 'Transcription'),
          )}
          <div className="px-4 pb-4">
            {isLoading ? (
              <div className="flex justify-center py-6">
                <Loader2 className={cn('h-4 w-4 animate-spin', sourdine)} />
              </div>
            ) : repliques.length > 0 ? (
              <div className="flex flex-col gap-3">
                {visibles.map((r, i) => {
                  const n = Math.max(0, locuteurs.indexOf(r.qui)) % TEINTES.length
                  return (
                    <div key={i} className="flex gap-2.5">
                      <span className={cn('mt-[7px] h-1.5 w-1.5 rounded-full flex-shrink-0', POINTS[n])} />
                      <div className="min-w-0">
                        {r.qui && <div className={cn('text-[11.5px] font-semibold', (darkMode ? TEINTES_SOMBRE : TEINTES)[n])}>{r.qui}</div>}
                        <p className={cn('text-[12.5px] leading-relaxed', texte)}>{r.texte}</p>
                      </div>
                    </div>
                  )
                })}
                {repliques.length > APERCU && (
                  <button
                    type="button"
                    onClick={() => setTout((v) => !v)}
                    className={cn('self-start text-[11.5px] font-medium', darkMode ? 'text-indigo-300 hover:text-indigo-200' : 'text-indigo-600 hover:text-indigo-700')}
                  >
                    {tout ? t('callDetail.showLess', 'Réduire') : t('callDetail.showFullTranscript', 'Voir toute la transcription')}
                  </button>
                )}
              </div>
            ) : data?.transcript ? (
              <p className={cn('text-[12.5px] leading-relaxed whitespace-pre-line', texte)}>{data.transcript}</p>
            ) : (
              <p className={cn('text-[12px]', sourdine)}>{t('callDetail.noTranscript', 'Pas de transcription pour cet appel.')}</p>
            )}
          </div>
        </section>
      </div>

      <div className={cn('p-3 border-t flex-shrink-0', darkMode ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white')}>
        {event.recording?.has_audio ? (
          <LecteurAppel phoneNumber={phoneNumber} callId={callId} darkMode={darkMode} dureeConnue={event.recording.duration} aiCallId={event.recording.ai_call_id} integre />
        ) : (
          <div className={cn('flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[12px]', darkMode ? 'bg-slate-800/60 text-slate-400' : 'bg-slate-50 text-slate-500')}>
            <span className={cn('h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0', darkMode ? 'bg-slate-700' : 'bg-slate-200')}>
              <Play className="h-3.5 w-3.5 ml-0.5 opacity-60" />
            </span>
            {t('callDetail.noRecording', 'Aucun enregistrement disponible')}
          </div>
        )}
      </div>
    </div>
  )
}
